import { useState, useEffect, useCallback } from 'react';
import { Card, Typography, DatePicker, Button, List, Empty, Space } from 'antd';
import { DownloadOutlined, MessageOutlined } from '@ant-design/icons';
import apiService from '../../services/api';
import { useTheme } from '../../context/ThemeContext';
import { COLOR_PRIMARY } from '../../utils/colors';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const EnquiryList = () => {
  const { isDarkMode } = useTheme();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);

  const buildParams = useCallback(() => {
    const params = {};
    if (dateRange[0]) params.startDate = dateRange[0].format('YYYY-MM-DD');
    if (dateRange[1]) params.endDate = dateRange[1].format('YYYY-MM-DD');
    return params;
  }, [dateRange]);

  const fetchEnquiries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.enquiries.getAll(buildParams());
      setEnquiries(response.data);
    } catch (err) {
      console.error('Failed to fetch enquiries', err);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await apiService.enquiries.export(buildParams());
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const start = dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : 'all';
      const end = dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : 'now';
      link.href = url;
      link.setAttribute('download', `enquiries-${start}-to-${end}.txt`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download enquiries', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <MessageOutlined style={{ fontSize: 20 }} />
          <Title level={4} style={{ margin: 0 }}>Raw Enquiries</Title>
        </div>
        <p style={{ color: '#666', marginBottom: 16 }}>
          WhatsApp messages received where no price was agreed yet. Filter by date and download as a text file.
        </p>

        <Space style={{ marginBottom: 20 }} wrap>
          <RangePicker
            value={dateRange}
            onChange={(range) => setDateRange(range || [null, null])}
            format="DD/MM/YYYY"
            allowClear
          />
          <Button type="primary" onClick={fetchEnquiries} loading={loading}>
            Filter
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            loading={downloading}
            disabled={enquiries.length === 0}
          >
            Download ({enquiries.length})
          </Button>
        </Space>

        {enquiries.length === 0 && !loading ? (
          <Empty description="No enquiries found for this date range" />
        ) : (
          <List
            loading={loading}
            dataSource={enquiries}
            rowKey="_id"
            pagination={{ pageSize: 20 }}
            renderItem={(item) => (
              <List.Item style={{ alignItems: 'flex-start' }}>
                <div style={{ width: '100%' }}>
                  <div style={{ marginBottom: 6 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {new Date(item.receivedAt).toLocaleString('en-GB')}
                      {item.from && ` · ${item.from}`}
                    </Text>
                  </div>
                  <pre style={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                    fontSize: 14,
                    background: isDarkMode ? 'rgba(255,255,255,0.06)' : '#f5f5f5',
                    padding: '10px 14px',
                    borderRadius: 6,
                    borderLeft: `3px solid ${COLOR_PRIMARY}`,
                  }}>
                    {item.rawText}
                  </pre>
                </div>
              </List.Item>
            )}
          />
        )}
      </Card>
    </div>
  );
};

export default EnquiryList;
